import type { Tournament, TournamentSaveData } from './types';
import type { AppStateController } from '../state/AppStateController';
import type {
  LeaderboardEntry,
  PlatformLeaderboards,
  PlatformTournaments,
  PlatformUser,
  TournamentData,
} from '../platform/Platform';
import { LoadingView } from '../../views/LoadingView';
import { TournamentDataSaver } from './TournamentDataSaver';
import { TournamentLeaderboardHandler } from './TournamentLeaderboardHandler';
import { getLocalized } from '../../libs/layouts/initPopulate';

export enum TournamentCategory {
  Global = 'Global',
  Friends = 'Friends',
  You = 'You',
}

interface TournamentServiceDependencies {
  globalTournamentUrl: string;
  state: AppStateController;
  leaderboards: PlatformLeaderboards;
  tournaments: PlatformTournaments;
}

export class TournamentService {
  private currentTournament: TournamentSaveData | null = null;

  private readonly dataSaver: TournamentDataSaver;

  private leaderboardHandler: TournamentLeaderboardHandler | null = null;

  private globalTournaments: Promise<TournamentSaveData[] | null> = Promise.resolve(null);

  private player: PlatformUser | null = null;

  constructor(private readonly dependencies: TournamentServiceDependencies) {
    this.dataSaver = new TournamentDataSaver(dependencies.state);
  }

  public async init(player: PlatformUser): Promise<void> {
    this.player = player;
    this.leaderboardHandler = new TournamentLeaderboardHandler({
      dataSaver: this.dataSaver,
      leaderboards: this.dependencies.leaderboards,
      currentPlayer: player,
    });
    this.globalTournaments = this.fetchGlobalConfig();
    await this.checkIfInTournament();
  }

  private checkIfInTournamentByContext(): void {
    const { tournaments } = this.dependencies;
    if (this.currentTournament && this.currentTournament.contextId !== tournaments.getContextID()) {
      this.currentTournament = null;
    }
  }

  private async checkIfInTournament(): Promise<void> {
    const { tournaments } = this.dependencies;

    const tournament = await tournaments.getCurrentTournament();

    if (!tournament) {
      this.currentTournament = null;
      return;
    }

    this.setCurrentTournament(tournament);
  }

  private setCurrentTournament(tournament: TournamentData): void {
    this.currentTournament = this.dataSaver.addTournament(tournament);
  }

  public async joinTournament(tournamentId: string): Promise<boolean> {
    const { tournaments } = this.dependencies;

    void LoadingView.getInstance().show();

    const tournamentData = await tournaments.joinTournament(tournamentId);
    if (tournamentData) this.setCurrentTournament(tournamentData);

    void LoadingView.getInstance().hide();

    return !!tournamentData;
  }

  public async increaseScore(score: number, withShare = false): Promise<void> {
    this.checkIfInTournamentByContext();
    if (!this.currentTournament) return;

    const { tournaments } = this.dependencies;

    this.currentTournament.userScore += score;
    this.dataSaver.saveData(this.currentTournament);

    void this.leaderboardHandler?.updateLeaderboardScore(this.currentTournament);

    if (withShare) {
      const shareSuccess = await this.shareTournament();
      if (shareSuccess) return;
    }

    const postSuccess = await tournaments.postScore(this.currentTournament.userScore);
    if (!postSuccess) await this.checkIfInTournament();
  }

  private async shareTournament(): Promise<boolean> {
    this.checkIfInTournamentByContext();
    if (!this.currentTournament) return false;

    const { tournaments } = this.dependencies;
    void LoadingView.getInstance().show(getLocalized('Loading Tournament'));
    const result = await tournaments.shareTournament(this.currentTournament.userScore);
    void LoadingView.getInstance().hide();
    return result;
  }

  public async createTournament(): Promise<boolean> {
    const { tournaments } = this.dependencies;
    const title = this.player?.name ? `${this.player?.name}'s Tournament` : 'Friendly Tournament';

    void LoadingView.getInstance().show();

    const tournament = await tournaments.createTournament({
      initialScore: 0,
      config: { title },
      data: {
        tournamentCreatorId: this.player?.id ?? null,
      },
    });

    void LoadingView.getInstance().hide();

    if (tournament)
      this.setCurrentTournament({
        ...tournament,
        creatorId: this.player?.id ?? null,
      });

    return !!tournament;
  }

  private async fetchGlobalConfig(): Promise<TournamentSaveData[] | null> {
    try {
      const response = await fetch(this.dependencies.globalTournamentUrl);

      const tournamentsConfig = (await response.json()) as TournamentData | TournamentData[];

      const tournaments = Array.isArray(tournamentsConfig) ? tournamentsConfig : [tournamentsConfig];

      return tournaments
        .filter((tournamentData) => this.isTournamentValid(tournamentData))
        .map((tournamentData) => this.dataSaver.addTournament(tournamentData));
    } catch (error) {
      console.warn(error);
      return null;
    }
  }

  public async getTournamentsByCategory(category: TournamentCategory): Promise<Tournament[]> {
    const categoryGetters: Record<TournamentCategory, () => Promise<Tournament[]>> = {
      [TournamentCategory.Global]: () => this.getGlobalTournaments(),
      [TournamentCategory.Friends]: () => this.getFriendsTournaments(),
      [TournamentCategory.You]: () => this.getUserCreatedTournaments(),
    };

    return categoryGetters[category]();
  }

  public async getGlobalTournaments(): Promise<Tournament[]> {
    const globalTournament = await this.globalTournaments;
    if (!globalTournament) return [];

    return Promise.resolve(
      globalTournament.map((tournament) => ({
        ...tournament,
        getLeaderboard: this.createLeaderboardGetter(tournament),
      })),
    );
  }

  private createLeaderboardGetter(tournamentData: TournamentSaveData): () => Promise<LeaderboardEntry[]> {
    return (
      this.leaderboardHandler?.createLeaderboardGetter(tournamentData) ??
      ((): Promise<LeaderboardEntry[]> => Promise.resolve([]))
    );
  }

  public async getFriendsTournaments(): Promise<Tournament[]> {
    return this.dataSaver
      .getTournaments()
      .filter((tournament) => !!tournament.creatorId && tournament.creatorId !== this.player?.id)
      .map((tournament) => ({
        ...tournament,
        getLeaderboard: this.createLeaderboardGetter(tournament),
      }));
  }

  public async getUserCreatedTournaments(): Promise<Tournament[]> {
    return this.dataSaver
      .getTournaments()
      .filter((tournament) => !!tournament.creatorId && tournament.creatorId === this.player?.id)
      .map((tournament) => ({
        ...tournament,
        getLeaderboard: this.createLeaderboardGetter(tournament),
      }));
  }

  private isTournamentValid(tournament: { endsAt: number }): boolean {
    return Date.now() < tournament.endsAt;
  }

  public getCurrentTournament(): TournamentData | null {
    this.checkIfInTournamentByContext();
    return this.currentTournament;
  }
}
